import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH5_QUESTIONS } from "../data/neetPhysicsCh5Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 5: Work, Energy and Power (NCERT Class 11).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Work & the Work-Energy Theorem", "Potential Energy & Conservation", "Power", "Collisions"];

const SECTION_COLORS = {
  "Work & the Work-Energy Theorem":  "#0891b2",
  "Potential Energy & Conservation": "#65a30d",
  "Power":                            "#f59e0b",
  "Collisions":                       "#e11d48",
};

const CONCEPTS = [
  // ── Work & the Work-Energy Theorem ──────────────────────────────────────────
  { section: "Work & the Work-Energy Theorem", title: "Work done by a constant force",
    explanation: "Work done by a constant force is W = F.d.cos(theta), where F and d are the magnitudes of the force and displacement and theta is the angle between them. Work is a scalar: it is positive when theta < 90 degrees (force has a component along the motion), negative when theta > 90 degrees (force opposes the motion), and exactly zero when theta = 90 degrees (force perpendicular to displacement).",
    example: `W = F . d . cos(theta)

A force of 10 N acts at 60 degrees to a 5 m displacement:
W = 10 x 5 x cos(60) = 50 x 0.5 = 25 J

Carrying a bag horizontally: gravity (vertical) is
perpendicular to displacement (horizontal) -> theta = 90,
so the work done by gravity is ZERO.` },
  { section: "Work & the Work-Energy Theorem", title: "Work done by a variable force (area under F-x graph)",
    explanation: "When a force's magnitude changes with position, work must be found by summing F.dx over tiny displacements: W = integral of F dx from xi to xf. Graphically, this is exactly the AREA under the force-position (F-x) graph between the initial and final positions, whatever the shape of the curve.",
    example: `W = area under F-x graph (xi to xf)

A spring-like force rises linearly from 0 N to 20 N as x
goes from 0 to 4 m (F = kx, k = 5 N/m):
Area of triangle = (1/2) x base x height
                 = (1/2) x 4 x 20 = 40 J
(matches (1/2)kx^2 = (1/2)(5)(4^2) = 40 J)` },
  { section: "Work & the Work-Energy Theorem", title: "Work-energy theorem",
    explanation: "The work-energy theorem states that the NET work done by all forces acting on a body equals its change in kinetic energy: W_net = KE_final - KE_initial. This holds whether the net force is constant or variable, and is a direct consequence of Newton's second law.",
    example: `W_net = KE_f - KE_i = (1/2)m.vf^2 - (1/2)m.vi^2

A 4 kg block starts from rest; a net force of 8 N acts
over a distance of 9 m (frictionless surface):
W_net = 8 x 9 = 72 J
72 = (1/2)(4)(vf^2) - 0  ->  vf^2 = 36  ->  vf = 6 m/s` },
  { section: "Work & the Work-Energy Theorem", title: "Nature of work: positive, negative, and zero work",
    explanation: "Work is POSITIVE when the force has a component in the direction of motion (adds kinetic energy), NEGATIVE when it opposes motion (removes kinetic energy, e.g. friction, braking), and ZERO when the force is perpendicular to displacement (e.g. gravity on a horizontally-moving object, or the centripetal force in uniform circular motion).",
    example: `A 2 kg box is lifted 1.5 m at constant velocity (g = 10 m/s^2):
Work by hand (upward force)   = +mgh = +2x10x1.5 = +30 J
Work by gravity (downward force) = -mgh = -2x10x1.5 = -30 J
Net work = 30 + (-30) = 0 J
-> consistent with work-energy theorem (constant velocity,
   so KE doesn't change, so W_net must be zero)` },

  // ── Potential Energy & Conservation ─────────────────────────────────────────
  { section: "Potential Energy & Conservation", title: "Kinetic energy",
    explanation: "Kinetic energy is the energy a body possesses due to its motion: KE = (1/2)mv^2, where m is mass and v is speed. It is always non-negative and depends on the square of speed -- doubling the speed quadruples the kinetic energy, for the same mass.",
    example: `KE = (1/2) m v^2

Car of mass 1000 kg moving at 20 m/s:
KE = (1/2)(1000)(20^2) = (1/2)(1000)(400) = 2 x 10^5 J

If speed doubles to 40 m/s:
KE = (1/2)(1000)(1600) = 8 x 10^5 J   (4x the original, since KE is proportional to v^2)` },
  { section: "Potential Energy & Conservation", title: "Gravitational potential energy (mgh)",
    explanation: "Gravitational potential energy near Earth's surface is U = mgh, where h is height measured from a chosen reference level (usually the ground). Only CHANGES in U are physically meaningful -- the reference level can be chosen arbitrarily for convenience.",
    example: `U = m . g . h

A 5 kg mass at height 10 m (g = 10 m/s^2):
U = 5 x 10 x 10 = 500 J

If height doubles to 20 m:
U = 5 x 10 x 20 = 1000 J   (U is proportional to h)` },
  { section: "Potential Energy & Conservation", title: "Elastic (spring) potential energy (½kx²)",
    explanation: "A stretched or compressed spring stores elastic potential energy U = (1/2)k.x^2, where k is the spring's force constant (N/m) and x is the displacement from its natural (unstretched) length. Like kinetic energy, this depends on the SQUARE of the displacement.",
    example: `U = (1/2) k x^2

Spring with k = 200 N/m compressed by x = 0.1 m:
U = (1/2)(200)(0.1^2) = (1/2)(200)(0.01) = 1 J

If compression doubles to x = 0.2 m:
U = (1/2)(200)(0.04) = 4 J   (4x the original, since U is proportional to x^2)` },
  { section: "Potential Energy & Conservation", title: "Conservative vs non-conservative forces",
    explanation: "A CONSERVATIVE force's work depends only on the initial and final positions, not the path taken -- so its work over any closed path is exactly zero, and it has an associated potential energy (gravity, spring force, electrostatic force). A NON-CONSERVATIVE force's work depends on the path length and dissipates mechanical energy, usually as heat (friction, air resistance) -- it has no potential energy function.",
    example: `Friction force = mu.m.g;  m = 2 kg, mu = 0.2, g = 10 m/s^2
Friction force = 0.2 x 2 x 10 = 4 N

Straight path, length 5 m: W_friction = -4 x 5  = -20 J
Curved path,   length 8 m: W_friction = -4 x 8  = -32 J
-> different work for different paths between the SAME two
   points -- proves friction is non-conservative` },
  { section: "Potential Energy & Conservation", title: "Conservation of mechanical energy",
    explanation: "When only conservative forces do work on a system (no friction or other dissipative forces), the total mechanical energy -- kinetic plus potential -- remains constant: KE + U = constant. As an object falls or a spring releases, potential energy converts to kinetic energy (and vice versa) but their sum never changes.",
    example: `KE_i + U_i = KE_f + U_f

Block released from rest at height h = 5 m on a
frictionless incline (g = 10 m/s^2):
mgh = (1/2)mv^2
(10)(5) = (1/2)v^2   [m cancels]
v^2 = 100  ->  v = 10 m/s at the bottom` },

  // ── Power ────────────────────────────────────────────────────────────────────
  { section: "Power", title: "Average power (P = W/t)",
    explanation: "Average power is the total work done (or total energy transferred) divided by the total time taken: P_avg = W/t, regardless of how the work rate varied within that interval. Power is measured in watts (W), where 1 W = 1 J/s.",
    example: `P_avg = W / t

A machine does 6000 J of work in 30 s:
P_avg = 6000 / 30 = 200 W` },
  { section: "Power", title: "Instantaneous power (P = F·v)",
    explanation: "Instantaneous power is the rate of doing work at a specific instant: P = F.v.cos(theta), the dot product of the force and the (instantaneous) velocity, where theta is the angle between them. If force and velocity are in the same direction, P = Fv; if perpendicular, P = 0.",
    example: `P = F . v . cos(theta)

A car engine's driving force is 500 N, car moves at
20 m/s in the same direction as the force:
P = 500 x 20 x cos(0) = 10,000 W = 10 kW

If the force were perpendicular to velocity (theta = 90):
P = 500 x 20 x cos(90) = 0 W` },
  { section: "Power", title: "Units of power (watt, kilowatt, horsepower)",
    explanation: "The SI unit of power is the watt (W = J/s); larger quantities use kilowatt (1 kW = 1000 W). A common non-SI unit is horsepower: 1 hp = 746 W. The commercial (and everyday electricity-billing) unit of ENERGY, the kilowatt-hour (kWh), equals the energy delivered by 1 kW of power sustained for 1 hour.",
    example: `1 hp = 746 W
2 hp = 2 x 746 = 1492 W ~ 1.49 kW

1 kWh = 1000 W x 3600 s = 3.6 x 10^6 J = 3.6 MJ` },
  { section: "Power", title: "Power in practical applications",
    explanation: "Power calculations often combine the work-energy relation with a rate: find the work (or energy) needed for a task, then divide by the time taken to complete it. This applies to pumps lifting water, engines driving vehicles against resistance, and motors doing mechanical work.",
    example: `Power = Work done / time taken

A pump lifts 200 kg of water through height 6 m every
minute (g = 10 m/s^2):
Work per minute = mgh = 200 x 10 x 6 = 12,000 J
Power = 12,000 J / 60 s = 200 W` },

  // ── Collisions ────────────────────────────────────────────────────────────────
  { section: "Collisions", title: "Elastic vs inelastic collisions",
    explanation: "In EVERY collision of an isolated system, total linear momentum is conserved. In a PERFECTLY ELASTIC collision, total kinetic energy is also conserved (no energy lost to heat, sound, or deformation). In an INELASTIC collision, momentum is still conserved, but some kinetic energy is converted into other forms -- a PERFECTLY INELASTIC collision is the extreme case where the colliding bodies stick together and move with a common velocity, losing the maximum possible kinetic energy consistent with momentum conservation.",
    example: `Always true:   m1.u1 + m2.u2 = m1.v1 + m2.v2   (momentum)
Elastic only:  (1/2)m1.u1^2 + (1/2)m2.u2^2
             = (1/2)m1.v1^2 + (1/2)m2.v2^2      (KE conserved)` },
  { section: "Collisions", title: "Perfectly inelastic collision",
    explanation: "When two bodies collide and stick together, momentum conservation gives a single common final velocity: v = (m1.u1 + m2.u2)/(m1+m2). The kinetic energy lost in the process equals the difference between initial and final kinetic energies, and is always positive (some KE is always lost) unless the two bodies were already moving with the same velocity.",
    example: `v = (m1.u1 + m2.u2) / (m1 + m2)

2 kg ball at 6 m/s strikes a stationary 3 kg ball; they
stick together:
v = (2x6 + 3x0)/(2+3) = 12/5 = 2.4 m/s

KE_initial = (1/2)(2)(6^2)   = 36 J
KE_final   = (1/2)(5)(2.4^2) = 14.4 J
KE lost = 36 - 14.4 = 21.6 J  (converted to heat/deformation)` },
  { section: "Collisions", title: "1D elastic collision velocity formulas",
    explanation: "For a head-on (1D) elastic collision between masses m1 (velocity u1) and m2 (velocity u2), solving momentum conservation together with kinetic energy conservation gives: v1 = [(m1-m2)u1 + 2.m2.u2]/(m1+m2) and v2 = [(m2-m1)u2 + 2.m1.u1]/(m1+m2). These follow from combining momentum conservation with the coefficient of restitution e = 1.",
    example: `v1 = [(m1-m2)u1 + 2.m2.u2] / (m1+m2)
v2 = [(m2-m1)u2 + 2.m1.u1] / (m1+m2)

1 kg ball at 4 m/s strikes a stationary 3 kg ball (u2=0):
v1 = [(1-3)(4) + 0] / (1+3) = -8/4 = -2 m/s
v2 = [0 + 2(1)(4)] / (1+3)  =  8/4 =  2 m/s

Check momentum: before = 1x4 = 4; after = 1x(-2)+3x2 = 4  OK
Check KE: before = (1/2)(1)(16) = 8 J
          after  = (1/2)(1)(4) + (1/2)(3)(4) = 2+6 = 8 J   OK` },
  { section: "Collisions", title: "Special cases of 1D elastic collisions",
    explanation: "Three important special cases of the 1D elastic collision formulas (target m2 initially at rest, u2 = 0): (1) EQUAL MASSES (m1=m2): velocities simply exchange -- v1=0, v2=u1. (2) VERY LIGHT PROJECTILE hitting a very heavy stationary target (m1<<m2): the projectile bounces straight back, v1 ~ -u1, and the heavy target barely moves, v2 ~ 0. (3) VERY HEAVY PROJECTILE hitting a very light stationary target (m1>>m2): the projectile barely slows, v1 ~ u1, and the light target flies off at roughly twice the projectile's speed, v2 ~ 2.u1.",
    example: `Equal masses (m1=m2), u2=0:  v1 = 0,     v2 = u1  (exchange)
Light ball hits heavy wall:    v1 ~ -u1,  v2 ~ 0    (rebounds)
Heavy truck hits light target: v1 ~ u1,   v2 ~ 2.u1 (target flung forward)

Example (equal masses, m1=m2=2 kg, u1=5 m/s, u2=1 m/s):
v1' = u2 = 1 m/s,  v2' = u1 = 5 m/s   (velocities exchange)
Momentum: before = 2(5)+2(1)=12; after = 2(1)+2(5)=12  OK` },
  { section: "Collisions", title: "Coefficient of restitution",
    explanation: "The coefficient of restitution e measures how 'bouncy' a collision is: e = (relative velocity of separation)/(relative velocity of approach) = (v2-v1)/(u1-u2). e = 1 for a perfectly elastic collision, e = 0 for a perfectly inelastic collision (bodies move together, zero separation speed), and 0 < e < 1 for a real, partially-inelastic collision.",
    example: `e = (v2 - v1) / (u1 - u2)

Ball dropped from height h1 = 5 m onto a stationary floor,
rebounds to height h2 = 1.25 m:
approach speed  u1 = sqrt(2.g.h1)
separation speed v1 = sqrt(2.g.h2)   (floor stays at rest)
e = v1/u1 = sqrt(h2/h1) = sqrt(1.25/5) = sqrt(0.25) = 0.5` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh5Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh5Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh5Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH5_QUESTIONS : NEET_PHYSICS_CH5_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 5</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Work, Energy and Power</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 11 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH5_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH5_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
